/**
 * useBidIntake — ask the bid-intake function to draft engine inputs from a
 * plain-English job description.
 *
 * The AI only fills the form; the deterministic engine still computes the
 * bid. Returns a status the UI must distinguish: 'ok' (inputs filled),
 * 'unavailable' (Anthropic outage — disclose, user fills manually), or
 * 'malformed' (model output unreadable — ask to rephrase).
 */

import { useMutation } from '@tanstack/react-query'
import { useSupabase }  from './useSupabase'
import type { BidInputs } from '@/lib/bidding/engine'

export type BidIntakeStatus = 'ok' | 'unavailable' | 'malformed'

export interface BidIntakeResult {
  status: BidIntakeStatus
  inputs: BidInputs | null
  notes:  string
}

export function useBidIntake(): ReturnType<typeof useMutation<BidIntakeResult, Error, string>> {
  const supabase = useSupabase()

  return useMutation<BidIntakeResult, Error, string>({
    mutationFn: async (description) => {
      const { data, error } = await supabase.functions.invoke('bid-intake', {
        body: { description },
      })
      if (error) throw error
      return data as BidIntakeResult
    },
  })
}
